/**
 * Сортировка таблиц: ОДИН механизм на все 38 таблиц приложения.
 *
 * ПОЧЕМУ ОДНО МЕСТО, А НЕ ПО СОРТИРОВКЕ В КАЖДОЙ ТАБЛИЦЕ. В приложении 37
 * элементов `<table className="data">` в тринадцати файлах (замер: `grep -c
 * "<table" components/*.tsx`). Сортировка, написанная в каждой на месте, — это
 * 38 мест, где однажды сравнят bigint как строку, и 38 мест, где забудут про
 * NULL. Оба дефекта не падают, а печатают неверный порядок, и заказчик увидит
 * их раньше нас. Здесь одна функция сравнения, один коллатор и один заголовок:
 * ошибка в них видна на всех таблицах сразу, а исправление доходит до всех.
 *
 * ================================================================= применение
 *
 * Обвязка таблицы — вызов хука и замена `<th>` на `<Th>`, больше ничего:
 *
 *     const S = useSortable(report.byPark, {
 *       park:    { kind: "text",   get: (p) => dbLabel(SENTINEL, p.park_name, p.park_name) },
 *       spend:   { kind: "number", get: (p) => (p.has_ad_data ? p.spend_aed_minor : null) },
 *       perConv: { kind: "number", get: (p) => {
 *         const conv = Number(p.conversations);
 *         return p.has_ad_data && conv > 0 ? Number(p.spend_aed_minor) / conv : null;
 *       } },
 *       revenue: { kind: "number", get: (p) => (p.has_pos_data ? p.revenue_aed_minor : null) },
 *     });
 *
 *     <thead>
 *       <tr>
 *         <Th sort={S} col="park">{t(UI.park)}</Th>
 *         <Th sort={S} col="spend" num>{t(TX.colAdSpendAed)}</Th>
 *         <Th sort={S} col="perConv" num>{t(TX.colCostPerConversation)}</Th>
 *         <Th sort={S} col="revenue" num>{t(TX.colPosRevenueAed)}</Th>
 *         <th />
 *       </tr>
 *     </thead>
 *     <tbody>{S.rows.map((p) => ( ... ))}</tbody>
 *
 * Колонка без сортировки (разделитель, кнопка «Bitrix ↗») остаётся обычным
 * `<th>` — заявлять её в карте колонок не нужно.
 *
 * КАРТА КОЛОНОК — ПРОСТОЙ ЛИТЕРАЛ, а не вызовы функций-конструкторов
 * (`byNumber(...)`), и это не вкус, а требование вывода типов: проверено, что
 * при обёртке в функцию TypeScript не может вывести тип строки из первого
 * аргумента внутрь замыкания и даёт `'p' is of type 'unknown'` — обвязке
 * пришлось бы писать `useSortable<Park, "park" | "spend" | …>(...)`, то есть
 * перечислять имена колонок дважды. С литералом всё выводится само, и
 * проверено, что компилятор ловит все три вида опечатки: `kind: "money"` →
 * «not assignable to type 'SortKind'», несуществующее поле в доступе →
 * «Property … does not exist», `col="parkk"` → «Did you mean "park"?».
 *
 * Общая таблица, которой строки приходят от разных экранов (например
 * `ConversionTable` в `components/screens.tsx`), объявляет карту на уровне
 * модуля с явной аннотацией:
 *
 *     const COLS: Record<"name" | "revenue", SortColumn<ConvRow>> = {
 *       name:    { kind: "text",   get: (r) => r.name },
 *       revenue: { kind: "number", get: (r) => r.revenue },
 *     };
 *
 * ====================================================== колонка — это ФУНКЦИЯ
 *
 * Доступ к значению задаётся функцией, а не именем поля. Это не гибкость на
 * будущее, а требование замера — по имени поля значительная часть колонок
 * сортировать НЕЧЕМ:
 *
 *   • `components/marketing.tsx:690, 692—694, 696—698` — три колонки считаются
 *     на месте рендера и в ответе сервера не существуют: цена обращения
 *     (`s / conv / 100`), цена лида (`s / p.leads / 100`), доля рекламы в
 *     выручке (`pctValue((100 * s) / rev, 2)`).
 *   • `components/screens.tsx:886—896, 899—907, 912—920` — строки пересобираются
 *     из другой формы ответа: `report.byChannel.map((r) => ({ name: …,
 *     reached: r.reached_handoff, … }))` перед передачей в общий
 *     `ConversionTable`. Поля таблицы и поля ответа — разные наборы.
 *   • `components/screens.tsx:889` — подпись переводится через `dbLabel(CHANNEL,
 *     r.channel_key, …)` ДО передачи в таблицу. Сортировка по `channel_key`
 *     дала бы порядок ключей базы, а человек видит слова и ждёт их порядка.
 *
 * ============================================== вид колонки объявляется ЯВНО
 *
 * `kind: "number"` или `kind: "text"` — руками, а не угадыванием по `typeof`. Postgres
 * отдаёт bigint и numeric СТРОКАМИ, а `::int` — числом, и это лежит рядом в
 * ОДНОЙ строке ответа. Замер на боевой базе (запрос по образцу
 * `server/src/api/marketing.ts:95—114`, разрез по паркам):
 *
 *     park_key          = "ll_dfc"                <string>
 *     park_name         = "Leo & Loona DFC Mall"  <string>
 *     spend_aed_minor   = "8258166"               <string>   ← bigint
 *     conversations     = "1835"                  <string>   ← bigint
 *     leads             = 38664                   <number>   ← ::int
 *     reached_handoff   = 350                     <number>   ← ::int
 *     revenue_aed_minor = "5075254443"            <string>   ← bigint
 *
 * Угадывание по `typeof` отправило бы `spend_aed_minor` в строковое сравнение,
 * а `leads` — в числовое, в одной и той же таблице. Замер на трёх настоящих
 * суммах в минорных единицах — "43600000", "1000", "9":
 *
 *     числом (верно):  436K AED  >  10 AED  >  0.09 AED
 *     строкой (ложь):  0.09 AED  >  436K AED  >  10 AED
 *
 * То есть девять филсов встали бы на первое место как самая крупная трата.
 * Объявление вида руками — единственное, что невозможно испортить молча:
 * неверный вид виден в порядке сразу и целиком.
 *
 * ================================================ деньги — в минорных единицах
 *
 * Доступ денежной колонки обязан возвращать поле `*_minor`, а НЕ результат
 * `money()`. `money()` умеет сокращение («436К», «5,08 млн»), и сравнивать
 * "436К" с "91 200" нечем: как строки они дают "436К" < "91 200" (проверено),
 * то есть 436 тысяч меньше 91 тысячи. Отформатированная строка вообще не число:
 * в ней разряды разделены узким пробелом, дробная часть — запятой, а суффикс
 * зависит от языка, и порядок таблицы менялся бы при переключении языка.
 *
 * ВЛЕЗАЮТ ЛИ МИНОРНЫЕ ЕДИНИЦЫ В `number` БЕЗ ПОТЕРЬ — да, с запасом в полтора
 * миллиона раз. Замер максимума, который может оказаться на экране: сумма кассы
 * за всю историю по всем паркам, `SELECT sum(revenue_aed_minor) FROM
 * mart.park_revenue_daily` = 5 530 683 439 минорных единиц (55,3 млн AED).
 * `Number.MAX_SAFE_INTEGER` = 9 007 199 254 740 991, запас ×1 628 587. Отдельно
 * проверено, что перевод строки в число точен: `Number("5075254443") ===
 * 5075254443` → true. Приводить bigint к `BigInt` не нужно и не надо: сравнение
 * `BigInt` медленнее, а выигрыш начнётся при кассе в 90 триллионов AED.
 *
 * =============================================== русский текст — через Collator
 *
 * Кириллица в `<` сравнивается по кодам UTF-16, а не по алфавиту: «Ё» это
 * U+0401, а «А» — U+0410, то есть заглавная «Ё» стоит ДО всей кириллицы, а
 * строчная «ё» (U+0451) — ПОСЛЕ неё. Замер на пяти словах:
 *
 *     наивное `<`  :  Ёлка < Азбука < Елка < яблоко < ёлка
 *     коллатор     :  Азбука < Елка < ёлка < Ёлка < яблоко
 *
 * Поэтому текст сравнивает `Intl.Collator`.
 *
 * КОЛЛАТОР В МОДУЛЬНОМ КЭШЕ, ОДИН НА ЯЗЫК. Замер на 1000 русских строк:
 *   • один коллатор на язык, из кэша            — 0,76 мс на сортировку
 *   • `new Intl.Collator(...)` на КАЖДОЕ сравнение — 39,57 мс
 * Кэш быстрее в 52 раза. Причина в том, что построение коллатора тянет таблицы
 * ICU, а сортировка 1000 строк делает около 9000 сравнений: без кэша это 9000
 * построений вместо одного.
 *
 * SENSITIVITY ПОДОБРАН ЗАМЕРОМ, А НЕ ПО ПАМЯТИ. Требовалось: «ё» на своём месте
 * («Елка» < «Ёлка») и регистр не влияет. Проверены все четыре варианта на
 * `ru`, `numeric: true`:
 *
 *     sensitivity   Елка↔Ёлка   елка↔Елка   Park 2↔Park 10
 *     base              0           0            -1          «ё» слилась с «е»
 *     accent           -1           0            -1          ← подходит
 *     case              0          -1            -1          «ё» слилась, регистр влияет
 *     variant          -1          -1            -1          регистр влияет
 *
 * Взято `accent`: различает «е»/«ё», не различает регистр. Побочно проверено,
 * что "елка" и "Елка" при нём равны, то есть сохраняют исходный взаимный
 * порядок (сортировка в V8 стабильна) — регистр не тасует строки.
 *
 * `numeric: true` — чтобы «Park 2» шёл перед «Park 10» (замер: `Park 1 < Park 2
 * < Park 10 < Park 20`). Он же делает безопасной сортировку ISO-дат и недель
 * текстовой колонкой: сегменты выровнены по позициям, и замер даёт правильный
 * порядок — `2025-12-31 < 2026-01-31 < 2026-09-02 < 2026-09-09 < 2026-09-10 <
 * 2026-10-01`. Даты в этом приложении приходят строкой (`::text`, см. правило
 * про UTC), поэтому `kind: "text"` для них — не обходной путь, а прямой.
 *
 * Язык берётся из `currentLang()`, а не приходит параметром, — тем же решением
 * и по той же причине, что в `lib/format.ts`: параметр означал бы место, где
 * его однажды забудут, и русская таблица сортировалась бы английским
 * коллатором. Смена языка перерисовывает всё дерево (`App.tsx:291 useLang()`),
 * и порядок пересчитывается сам.
 *
 * ============================================================ NULL — в КОНЦЕ
 *
 * Пустое значение всегда внизу, в обоих направлениях, и проверяется ДО
 * применения знака. Замер на том, что печатают ячейки:
 *   `Number("—")` = NaN, `Number("no access")` = NaN, `Number(null)` = 0.
 * То есть при наивном сравнении парк без рекламного кабинета встал бы либо в
 * непредсказуемое место (NaN ломает сравнение), либо ровно между настоящими
 * нулями — и «нет доступа» читалось бы как «ноль расхода».
 *
 * ОТСЮДА ОБЯЗАННОСТЬ ФУНКЦИИ ДОСТУПА: возвращать `null` везде, где ячейка
 * печатает прочерк или фразу-заменитель. В `components/marketing.tsx:688—698`
 * это `t(TX.noAdAccount)` («no ad account»), `t(TX.noSyrveAccess)` («no Syrve
 * access») и «—» у трёх вычисляемых колонок при нулевом знаменателе. Пустая
 * строка приравнена к `null` — ячейка с "" печатает то же, что прочерк. NaN на
 * числовой колонке тоже уходит в конец, а не роняет порядок: доступ, вернувший
 * непарсящееся значение, — это дефект обвязки, и он должен быть виден как
 * «строки внизу», а не как случайная перестановка всей таблицы.
 *
 * ================================== третье состояние — ИСХОДНЫЙ ПОРЯДОК
 *
 * Клик третий раз возвращает массив КАК ПРИШЁЛ (тот же массив, не копию: вход
 * никогда не мутируется). Не «по первой колонке», не «по id» — именно исходный,
 * потому что серверный порядок осмыслен и воспроизвести его на клиенте нечем:
 *
 *   • `server/src/api/dashboard.ts:429` — `ORDER BY p.sort`: порядок парков
 *     задан заказчиком в справочнике. Поля `sort` в ответе нет, и вывести его
 *     из имени парка невозможно.
 *   • `server/src/api/digest.ts:221` — `ORDER BY finished_at NULLS FIRST`:
 *     отставшие загрузки сверху, никогда не запускавшиеся — самыми первыми.
 *     Это порядок «на что смотреть», а не порядок величины.
 *   • `server/src/api/sales.ts:97` — `ORDER BY week_start DESC`: недели по
 *     убыванию, свежая сверху.
 *   • `server/src/api/digest.ts:255` и `server/src/api/operations.ts:209` —
 *     `ORDER BY dd.date_of_event`: ближайшие праздники по дате.
 *   • `server/src/api/sales.ts:128` — `ORDER BY park_name, age_order`:
 *     возрастные корзины пайплайна в осмысленном порядке внутри парка;
 *     `age_order` есть в ответе, но составной порядок «парк, потом корзина» не
 *     выражается одной колонкой.
 *
 * =========================== первый клик: число — DESC, текст — ASC
 *
 * Серверные порядки метрик почти все `DESC` (`ORDER BY leads DESC`,
 * `spend_aed_minor DESC`, `reached_handoff DESC`, `revenue_aed_minor DESC`,
 * `deals DESC`, `cycles DESC` — по замеру, большинство `ORDER BY` в
 * `server/src/api/`). Первый клик по числовой колонке, совпадающий с привычным
 * видом, делает механизм незаметным: человек кликает и получает то, что и так
 * видел, только по другой метрике. Текст же читают по алфавиту сверху вниз,
 * поэтому текстовая колонка начинает с `ASC`.
 *
 * ====================================================== useMemo — НЕТ, и почему
 *
 * Сортировка считается на каждый рендер. Обоснование числом: замер на
 * максимальном РЕАЛЬНОМ объёме приложения. Самая длинная таблица без
 * пагинации — кандидаты когорты (`server/src/api/cohorts.ts:106—197`, без
 * LIMIT), 2385 строк за всю историю (замер на боевой базе). На 2400 строках
 * сортировка стоит 0,24 мс по числу и 2,0 мс по тексту с коллатором. Кадр —
 * 16,7 мс. Остальные таблицы короче на порядок: `LIMIT 400` у поиска путей,
 * `LIMIT 200` у ближайших праздников, 100 у страницы лидов, 30 у менеджеров,
 * 6 у парков — там 0,005…0,03 мс.
 *
 * Против этих 2 мс стоит цена памоизации: массив зависимостей обязан включать
 * замыкания доступа, а они создаются в разметке заново на каждый рендер и
 * держат объект отчёта. Правильный список зависимостей означал бы пересчёт
 * всё равно (замыкания новые каждый рендер), а «оптимизированный» — забытую
 * зависимость и молча устаревшую таблицу: цифры новые, порядок от прошлого
 * отчёта. Это дефект того же класса, который в этом проекте вычищают весь
 * месяц, и 2 мс за его отсутствие — цена, которую стоит платить. Отдельно:
 * язык в зависимости не попал бы никогда (он читается из модуля), и после
 * переключения языка русская таблица осталась бы в английском порядке.
 *
 * ================================================== доступность и печать
 *
 * Заголовок — `<button>` внутри `<th>`: кликается с клавиатуры, объявляется
 * скринридером как кнопка и имеет подсказку о том, что сделает СЛЕДУЮЩИЙ клик.
 * `aria-sort` стоит на `<th>` (по стандарту — на ячейке заголовка колонки, не
 * на кнопке) и принимает `ascending` / `descending` / `none`.
 *
 * Значок направления — свой SVG в этом файле, а не иконка из `chrome.tsx`:
 * стрелок в наборе `ICON` нет, а чужой файл править нельзя. Обе стрелки
 * рисуются всегда, неактивная — цветом `--line-2`: ширина заголовка не скачет
 * при клике, и видно, что колонка вообще сортируемая. На значке класс
 * `noprint` — в `theme.css:1663—1668` он в `@media print` получает
 * `display: none !important`, и на печатном экране (кнопка «Print screen»)
 * значков не будет.
 *
 * ============================== где клиентская сортировка была бы ЛОЖЬЮ
 *
 * У таблицы, показывающей ЧАСТЬ выборки, клиентская сортировка отсортирует
 * только видимые строки, а человек прочтёт верхнюю как максимум по всей
 * выборке. Это не неудобство, а неверная цифра в отчёте. Найденные места:
 *
 *   • `components/leadList.tsx` — панель списка лидов: серверная пагинация по
 *     100 (`api.leads(filters, slice, 100, offset)`), всего лидов 88 480.
 *     Сортировка страницы дала бы «самую крупную сделку» из ста случайных.
 *   • `components/marketing.tsx:804` — `byCampaign.slice(0, 40)` от `LIMIT 100`
 *     при 267 кампаниях в базе.
 *   • Ближайшие праздники на Operations (`slice(0, 60)` от `LIMIT 200`) и в
 *     дайджесте (`slice(0, 12)` от `LIMIT 40`) — обе таблицы 19.10.2026
 *     заменены числом со списком сделок под ним.
 *   • `components/widgets.tsx:406` — `rows.slice(0, 10)` в разрезе по каналам.
 *   • Таблицы из запросов с `LIMIT` без «показать ещё»: менеджеры
 *     (`operations.ts:359`, `LIMIT 30`), петли пути (`journey.ts:85, 108, 121`
 *     — `LIMIT 12 / 10 / 15`), отставшие загрузки (`digest.ts:222`, `LIMIT 8`),
 *     поиск людей (`journeyExplorer.ts:141`, `LIMIT 25` с признаком
 *     `truncated`), история настроек (`settings.ts:43`, `LIMIT 20`).
 *
 * РЕШЕНИЕ: механизм ПРИНИМАЕТ серверную сортировку, а не отключается. Отказ
 * означал бы, что в самой нужной таблице — списке лидов — фичи нет, а именно
 * там её и просят. Обвязка такой таблицы передаёт `onSort`: хук держит
 * состояние и рисует заголовки как обычно, но строки НЕ переупорядочивает —
 * порядок приходит с сервера следующим запросом. Если таблица помечена
 * `partial: true`, но `onSort` не передан, заголовки становятся НЕкликабельными
 * с подсказкой о причине: незавершённая обвязка видна как выключенная
 * сортировка, а не как совравшая.
 *
 * ================================================== что проверено на поведении
 *
 * Логика сравнения и переключения прогнана на данных по образцу разреза по
 * паркам (два парка с рекламой, один без кабинета, один без кассы). Проверено:
 * первый клик по числу даёт DESC, по тексту ASC; парк без доступа стоит внизу
 * и по возрастанию, и по убыванию; третий клик возвращает ТОТ ЖЕ массив, а
 * входной массив не мутирован ни на одном шаге; вычисляемая колонка с делением
 * на ноль уходит в конец; фраза-заменитель, просочившаяся мимо доступа, даёт
 * NaN и тоже уходит в конец, а не перемешивает таблицу; «Park 2» перед
 * «Park 10»; ISO-даты в обе стороны; в кэше ровно два коллатора после работы
 * на обоих языках.
 */

import { useState } from "react";
import { currentLang, t, type L, type Lang } from "../i18n/lang.ts";

/** Проза этого механизма. Один блок в начале файла — см. App.tsx. */
const TX = {
  sortAscending: { ru: "Сортировать по возрастанию", en: "Sort ascending" },
  sortDescending: { ru: "Сортировать по убыванию", en: "Sort descending" },
  restoreOrder: { ru: "Вернуть исходный порядок", en: "Back to the original order" },
  partialTable: {
    ru: "Сортировка выключена: таблица показывает только часть выборки, и порядок был бы неверным",
    en: "Sorting is off here: the table shows only part of the selection, so the order would mislead",
  },
} satisfies Record<string, L>;

/* ------------------------------------------------------------- вид и колонки */

/**
 * Значение, по которому сравниваем. Строка здесь — не небрежность: bigint и
 * numeric приезжают из Postgres строками, и заставлять обвязку писать
 * `Number(...)` в каждом доступе значило бы 38 мест, где однажды забудут.
 */
export type SortValue = number | string | null | undefined;

/** Вид колонки. Объявляется руками — см. заголовок файла. */
export type SortKind = "number" | "text";

/**
 * Доступ к значению строки. Именно ФУНКЦИЯ, а не имя поля: значительная часть
 * колонок в приложении считается на месте рендера или переводится до передачи
 * в таблицу, и по имени поля их сортировать нечем — замеры в заголовке файла.
 */
export type SortAccess<Row> = (row: Row) => SortValue;

/**
 * Колонка: вид сравнения плюс доступ к значению.
 *
 * `kind: "number"` — деньги В МИНОРНЫХ единицах (`*_minor`), доли (`*_pct`),
 * счётчики, вычисляемые метрики. Прочерк, «нет доступа» и любой другой
 * заменитель в ячейке обязаны дать `null`.
 *
 * `kind: "text"` — сравнивается коллатором языка интерфейса, поэтому доступ
 * должен возвращать то, что ЧЕЛОВЕК ВИДИТ в ячейке (после `dbLabel`/`t`), а не
 * ключ базы: порядок ключей и порядок слов — разные порядки.
 */
export type SortColumn<Row> = {
  readonly kind: SortKind;
  readonly get: SortAccess<Row>;
};

/* ---------------------------------------------------------------- коллатор */

/**
 * Коллаторы по языку. Модульный кэш, а не переменная: языков два, оба нужны
 * одновременно после переключения — старые строки ещё на экране. Замер выигрыша
 * и подбор `sensitivity` — в заголовке файла.
 */
const COLLATORS = new Map<Lang, Intl.Collator>();

function collator(): Intl.Collator {
  const lang = currentLang();
  const cached = COLLATORS.get(lang);
  if (cached !== undefined) return cached;
  const made = new Intl.Collator(lang === "ru" ? "ru-RU" : "en-US", {
    sensitivity: "accent",
    numeric: true,
  });
  COLLATORS.set(lang, made);
  return made;
}

/* --------------------------------------------------------- функция сравнения */

/** Пусто — то, что ячейка печатает прочерком: сортировать нечем. */
function isBlank(v: SortValue): boolean {
  return v === null || v === undefined || v === "";
}

/**
 * Сравнение двух значений. Чистая функция: одни и те же аргументы дают один и
 * тот же ответ, состояния не читает (кроме языка, который у документа один).
 *
 * `dir` — 1 по возрастанию, -1 по убыванию. Пустые значения проверяются ДО
 * применения знака, поэтому уходят в конец в ОБОИХ направлениях.
 */
export function compareValues(kind: SortKind, dir: 1 | -1, a: SortValue, b: SortValue): number {
  const aBlank = isBlank(a);
  const bBlank = isBlank(b);
  if (aBlank || bBlank) return aBlank && bBlank ? 0 : aBlank ? 1 : -1;

  if (kind === "number") {
    const x = Number(a);
    const y = Number(b);
    /*
     * NaN — тот же случай, что пусто: доступ вернул не число. Уводим в конец, а
     * не бросаем в сравнение: NaN в компараторе не роняет `sort`, а тихо
     * перемешивает соседей, и дефект обвязки стал бы невидимым.
     */
    const xBad = !Number.isFinite(x);
    const yBad = !Number.isFinite(y);
    if (xBad || yBad) return xBad && yBad ? 0 : xBad ? 1 : -1;
    return x < y ? -dir : x > y ? dir : 0;
  }

  return dir * collator().compare(String(a), String(b));
}

/* ------------------------------------------------------------------- состояние */

export type SortState<K extends string> = {
  readonly col: K;
  /** 1 — по возрастанию, -1 — по убыванию. */
  readonly dir: 1 | -1;
};

/**
 * Направление первого клика: число — DESC, текст — ASC. Обоснование замером
 * серверных порядков — в заголовке файла.
 */
function firstDir(kind: SortKind): 1 | -1 {
  return kind === "number" ? -1 : 1;
}

/**
 * Три состояния по кругу: первый клик, обратный, исходный порядок.
 * `null` — исходный: массив отдаётся как пришёл.
 */
function nextState<K extends string>(
  cur: SortState<K> | null,
  col: K,
  kind: SortKind,
): SortState<K> | null {
  const first = firstDir(kind);
  if (cur === null || cur.col !== col) return { col, dir: first };
  if (cur.dir === first) return { col, dir: first === 1 ? -1 : 1 };
  return null;
}

export type SortOptions<K extends string> = {
  /**
   * Таблица показывает только ЧАСТЬ выборки: страницу, `slice(0, N)` или
   * результат запроса с `LIMIT` без «показать ещё». Без `onSort` заголовки
   * становятся некликабельными — см. раздел про ложь в заголовке файла.
   */
  readonly partial?: boolean;
  /**
   * Серверная сортировка. Если передана — хук НЕ переупорядочивает строки, а
   * сообщает выбор наверх; порядок обязан прийти следующим запросом.
   * Вызывается и с `null` (третий клик — исходный серверный порядок).
   */
  readonly onSort?: (sort: SortState<K> | null) => void;
};

export type Sortable<Row, K extends string> = {
  /** Строки в нужном порядке. При исходном порядке — ТОТ ЖЕ массив, что на входе. */
  readonly rows: readonly Row[];
  readonly state: SortState<K> | null;
  /** Для `<Th>`. Обвязке звать не нужно. */
  readonly toggle: (col: K) => void;
  /** Для `<Th>`: вид колонки нужен подсказке о следующем клике. */
  readonly kindOf: (col: K) => SortKind;
  /** Для `<Th>`: заголовки выключены, потому что видна часть выборки. */
  readonly inert: boolean;
};

/**
 * Хук сортировки таблицы.
 *
 * `columns` — карта «имя колонки → доступ». Имена свободные, компилятор берёт
 * их из литерала объекта и проверяет `col=` у заголовков по ним.
 */
export function useSortable<Row, K extends string>(
  rows: readonly Row[],
  columns: Readonly<Record<K, SortColumn<Row>>>,
  options: SortOptions<K> = {},
): Sortable<Row, K> {
  const [state, setState] = useState<SortState<K> | null>(null);
  const { partial = false, onSort } = options;

  /* Серверная сортировка: состояние держим для вида, порядок — не наш. */
  const server = onSort !== undefined;
  const inert = partial && !server;

  const kindOf = (col: K): SortKind => columns[col].kind;

  const toggle = (col: K): void => {
    const next = nextState(state, col, kindOf(col));
    setState(next);
    if (onSort !== undefined) onSort(next);
  };

  let ordered: readonly Row[] = rows;
  if (state !== null && !server && !inert) {
    const { kind, get } = columns[state.col];
    const dir = state.dir;
    /* Копия: исходный массив — единственный носитель серверного порядка. */
    ordered = [...rows].sort((a, b) => compareValues(kind, dir, get(a), get(b)));
  }

  return { rows: ordered, state, toggle, kindOf, inert };
}

/* -------------------------------------------------------------- заголовок */

/**
 * Стиль кнопки-заголовка. Инлайном, а не классом: `theme.css` не мой файл, а
 * подходящего класса в нём нет (`.btn-ghost` рисует пилюлю с рамкой — в шапке
 * таблицы это мусор). Нужный класс назван в отчёте.
 *
 * `font: inherit` обязателен: у элементов формы шрифт свой, и заголовок
 * потерял бы 10px/700/uppercase из `table.data th`. Разрядка и регистр
 * наследуются сами. Обводку фокуса не снимаем — она от браузера.
 */
const HEAD_BUTTON: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  margin: 0,
  padding: 0,
  border: 0,
  background: "none",
  color: "inherit",
  font: "inherit",
  textAlign: "inherit",
  cursor: "pointer",
};

/**
 * Значок направления: две стрелки, активная — цветом текста.
 *
 * Обе рисуются всегда — иначе ширина колонки скачет при клике, и таблица
 * дёргается на каждое переключение. Заодно видно, что колонка сортируемая, до
 * первого клика. `aria-hidden`: состояние уже сказано через `aria-sort` на
 * `<th>`, второй раз озвучивать нечего.
 */
function SortGlyph({ dir }: { dir: 0 | 1 | -1 }): React.ReactElement {
  /* Цвета из темы, а не свои: неактивная стрелка обязана гаснуть вместе с
     линейками таблицы при смене светлой темы на тёмную. */
  const up = dir === 1 ? "var(--ink)" : "var(--line-2)";
  const down = dir === -1 ? "var(--ink)" : "var(--line-2)";
  return (
    <svg
      className="noprint"
      width="7"
      height="11"
      viewBox="0 0 7 11"
      aria-hidden="true"
      focusable="false"
      style={{ flex: "none" }}
    >
      <path d="M3.5 0 7 4H0z" fill={up} />
      <path d="M3.5 11 0 7h7z" fill={down} />
    </svg>
  );
}

/**
 * Заголовок сортируемой колонки. Ставится вместо `<th>`; `num` включает то же
 * выравнивание вправо, что даёт класс `num` в `theme.css`.
 */
export function Th<Row, K extends string>({
  sort,
  col,
  num = false,
  style,
  children,
}: {
  sort: Sortable<Row, K>;
  col: K;
  /** Числовая колонка: выравнивание вправо и табличные цифры, как у `th.num`. */
  num?: boolean;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}): React.ReactElement {
  const cls = num ? "num" : undefined;

  /*
   * Часть выборки без серверной сортировки: обычный заголовок с объяснением.
   * `aria-sort` не ставим — колонка не сортируемая, и объявлять её сортируемой
   * скринридеру было бы тем же обманом, что и отсортировать страницу.
   */
  if (sort.inert) {
    return (
      <th className={cls} style={style} title={t(TX.partialTable)}>
        {children}
      </th>
    );
  }

  const dir: 0 | 1 | -1 = sort.state !== null && sort.state.col === col ? sort.state.dir : 0;
  const ariaSort = dir === 0 ? "none" : dir === 1 ? "ascending" : "descending";

  /* Подсказка про СЛЕДУЮЩИЙ клик: она полезнее, чем повтор текущего состояния. */
  const upcoming = nextState(sort.state, col, sort.kindOf(col));
  const hint =
    upcoming === null
      ? TX.restoreOrder
      : upcoming.dir === 1
        ? TX.sortAscending
        : TX.sortDescending;

  /*
   * У числовой колонки значок стоит СЛЕВА от подписи. Иначе он вклинивается
   * между правым краем ячейки и подписью, и подпись перестаёт стоять над
   * столбцом цифр, который выровнен вправо классом `num`.
   */
  const label = <span>{children}</span>;
  const glyph = <SortGlyph dir={dir} />;

  return (
    <th className={cls} style={style} aria-sort={ariaSort}>
      <button type="button" style={HEAD_BUTTON} title={t(hint)} onClick={() => sort.toggle(col)}>
        {num ? glyph : label}
        {num ? label : glyph}
      </button>
    </th>
  );
}
